import { FC, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AddressAutofill } from "@mapbox/search-js-react";
import { useGPSLocation } from "../map/hooks/useGPSLocation";
import { Company } from "../types";
import { apiClient } from "../../utils/apiClient/apiClient";
import Header from "../Header";
import Footer from "../Footer";
import "./LocationSelector.css";

interface LocationSelectorProps {
  projectId: string;
}

export const LocationSelector: FC<LocationSelectorProps> = ({ projectId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    location: gpsLocation,
    loading: gpsLoading,
    error: gpsError,
  } = useGPSLocation();

  // Company selection state
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [companiesError, setCompaniesError] = useState<string | null>(null);

  // Privacy modal state

  // Fetch companies on component mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setCompaniesLoading(true);
        const companiesData = await apiClient.getCompanies();
        setCompanies(companiesData);
        setCompaniesError(null);
      } catch (error) {
        console.error("Failed to fetch companies:", error);
        setCompaniesError("Failed to load companies. Please try again.");
      } finally {
        setCompaniesLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  const handleRetrieve = (res: any) => {
    if (!selectedCompany) {
      alert("Please select a company first.");
      return;
    }

    const feature = res.features[0];
    const [longitude, latitude] = feature.geometry.coordinates;

    navigate(`/projects/${projectId}/map`, {
      state: {
        latitude,
        longitude,
        address: feature.properties.full_address || feature.place_name,
        company: selectedCompany,
      },
    });
  };

  const handleGPSLocation = async () => {
    if (!selectedCompany) {
      alert("Please select a company first.");
      return;
    }

    if (gpsLocation) {
      let addr = "";

      try {
        const res = await fetch(
          `https://api.mapbox.com/search/geocode/v6/reverse?permanent=true&types=address&longitude=${
            gpsLocation.longitude
          }&latitude=${gpsLocation.latitude}&access_token=${
            import.meta.env.VITE_MAPBOX_TOKEN || ""
          }`,
          {
            method: "GET",
          }
        );

        if (res.ok) {
          const body = await res.json();

          // Extract the address from the response
          if (body.features && body.features.length > 0) {
            const feature = body.features[0];
            addr = feature.properties.full_address || feature.place_name || "";
          }
        } else {
          console.error(
            "Reverse geocoding failed:",
            res.status,
            res.statusText
          );
        }
      } catch (error) {
        console.error("Error during reverse geocoding:", error);
      }

      navigate(`/projects/${projectId}/map`, {
        state: {
          latitude: gpsLocation.latitude,
          longitude: gpsLocation.longitude,
          address: addr,
          company: selectedCompany,
        },
      });
    }
  };

  return (
    <div className="location-selector-page">
      <Header />
      <div className="location-selector">
        <div className="location-selector-content">
          <h1>{t("locationSelector.title")}</h1>
          <p className="subtitle">
            Select a company and set your project's location
          </p>

          {/* Company Selection Section */}
          <div className="company-selection-section mb-4">
            <div className="option-icon mb-3">🏢</div>
            <h3>Select Company</h3>
            <p className="help-text mb-3">
              Choose the company this project belongs to. This will determine
              the available inventory.
            </p>

            {companiesLoading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading companies...</span>
                </div>
                <p className="mt-2 text-muted">Loading companies...</p>
              </div>
            ) : companiesError ? (
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {companiesError}
              </div>
            ) : companies.length === 0 ? (
              <div className="alert alert-warning">
                <i className="bi bi-info-circle me-2"></i>
                No companies found. Please create a company first in the Admin
                section.
              </div>
            ) : (
              <div className="company-selector">
                <select
                  className="form-select form-select-lg"
                  value={selectedCompany?.company_id || ""}
                  onChange={(e) => {
                    const company = companies.find(
                      (c) => c.company_id === e.target.value
                    );
                    setSelectedCompany(company || null);
                  }}
                  required
                >
                  <option value="" disabled>
                    Select a company...
                  </option>
                  {companies.map((company) => (
                    <option key={company.company_id} value={company.company_id}>
                      {company.name}
                    </option>
                  ))}
                </select>
                {selectedCompany && (
                  <div className="selected-company-info mt-3 p-3 bg-light rounded">
                    <h6>{selectedCompany.name}</h6>
                    {selectedCompany.description && (
                      <p className="mb-1">{selectedCompany.description}</p>
                    )}
                    {selectedCompany.contact_email && (
                      <small className="text-muted">
                        Contact: {selectedCompany.contact_email}
                      </small>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="option-divider">
            <span>and</span>
          </div>

          {/* Location Selection Section */}
          <div className="location-selection-section">
            <h3>Set Project Location</h3>
            <p className="subtitle">
              Choose how you want to set your project's location
            </p>

            <div className="location-options">
              <button
                className={`gps-option ${gpsLoading ? "loading" : ""} ${
                  gpsError ? "error" : ""
                }`}
                onClick={handleGPSLocation}
                disabled={!selectedCompany || !gpsLocation || gpsLoading}
              >
                <div className="option-icon">📍</div>
                <div className="option-content">
                  <h3>{t("locationSelector.useMyLocation")}</h3>
                  <p>
                    {gpsLoading
                      ? t("locationSelector.gettingLocation")
                      : gpsError
                      ? gpsError
                      : gpsLocation
                      ? t("locationSelector.clickToUseLocation")
                      : t("locationSelector.locationNotAvailable")}
                  </p>
                </div>
              </button>

            <div className="option-divider">
              <span>{t("locationSelector.or")}</span>
            </div>

              <div className="address-option">
                <div className="option-icon">🏠</div>
                <div className="option-content">
                  <h3>Enter Address</h3>
                  <p>Type an address to find your project location</p>
                  <div
                    className={`address-input-container ${
                      !selectedCompany ? "disabled" : ""
                    }`}
                  >
                    {/* @ts-ignore */}
                    <AddressAutofill
                      accessToken={import.meta.env.VITE_MAPBOX_TOKEN || ""}
                      onRetrieve={handleRetrieve}
                    >
                      <input
                        name="address"
                        placeholder={
                          !selectedCompany
                            ? "Please select a company first"
                            : "Start typing an address..."
                        }
                        autoComplete="street-address"
                        className="address-input"
                        disabled={!selectedCompany}
                      />
                    </AddressAutofill>
                  </div>
                  <p className="help-text">
                    Choose an address from the suggestions to continue to your
                    project map
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};
